import { useMemo, useState, useEffect, useCallback } from "react";
import { Header } from "@/components/Header";
import { NoBusinessGuard } from "@/components/NoBusinessGuard";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Lightning, SpinnerGap, BookmarkSimple, Check, Copy, CaretDown } from "@phosphor-icons/react";
import { AnimatePresence, motion } from "framer-motion";

/**
 * SurfaceGeneratorPage — shared builder shell with dynamic context fields.
 *
 * Each builder page passes a `contextFields` array describing platform/mode-specific inputs.
 * Fields appear/hide based on the current platform and mode selections (progressive disclosure).
 *
 * Field config shape:
 * {
 *   key: string,              // unique state key
 *   label: string,            // display label
 *   type: "text"|"textarea"|"date"|"select"|"number"|"toggle"|"chips",
 *   placeholder?: string,
 *   options?: string[],       // for select/chips
 *   showWhen?: { always?: bool, mode?: string[], platform?: string[] },
 *   hint?: string,            // small helper text
 *   section?: string,         // group header
 *   half?: bool,              // true = half width in grid
 * }
 */

export default function SurfaceGeneratorPage({
  badge,
  title,
  description,
  contentType,
  platformLabel,
  platforms,
  modeLabel,
  modes,
  objectiveLabel,
  objectives,
  helperText,
  contextFields = [],
}) {
  const { api } = useAuth();
  const [topic, setTopic] = useState("");
  const [audience, setAudience] = useState("");
  const [platform, setPlatform] = useState(platforms?.[0] || "");
  const [mode, setMode] = useState(modes?.[0] || "");
  const [objective, setObjective] = useState(objectives?.[0] || "");
  const [context, setContext] = useState("");
  const [draft, setDraft] = useState("");
  const [fullResponse, setFullResponse] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [availableModels, setAvailableModels] = useState([]);
  const [aiModel, setAiModel] = useState("");
  const [fieldValues, setFieldValues] = useState({});
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    api.get("/models/available").then((r) => {
      const models = r.data?.models || [];
      setAvailableModels(models);
      if (models.length > 0) setAiModel(prev => prev || models[0]);
    }).catch(() => {});
  }, [api]);

  const updateField = useCallback((key, value) => {
    setFieldValues(prev => ({ ...prev, [key]: value }));
  }, []);

  const toggleChip = useCallback((key, chip) => {
    setFieldValues(prev => {
      const current = prev[key] || [];
      return { ...prev, [key]: current.includes(chip) ? current.filter(c => c !== chip) : [...current, chip] };
    });
  }, []);

  const isFieldVisible = useCallback((field) => {
    const sw = field.showWhen;
    if (!sw || sw.always) return true;
    const modeMatch = !sw.mode || sw.mode.includes(mode);
    const platformMatch = !sw.platform || sw.platform.includes(platform);
    return modeMatch && platformMatch;
  }, [mode, platform]);

  const visibleFields = useMemo(() => contextFields.filter(isFieldVisible), [contextFields, isFieldVisible]);

  const goal = useMemo(() => {
    const modePart = mode ? `${modeLabel}: ${mode}` : "";
    const objectivePart = objective ? `${objectiveLabel}: ${objective}` : "";
    return [modePart, objectivePart].filter(Boolean).join(" | ");
  }, [mode, objective, modeLabel, objectiveLabel]);

  const buildStructuredContext = useCallback(() => {
    const parts = [];
    visibleFields.forEach(field => {
      const val = fieldValues[field.key];
      if (val === undefined || val === null || val === "") return;
      if (field.type === "toggle" && !val) return;
      if (field.type === "chips") {
        if (Array.isArray(val) && val.length > 0) parts.push(`${field.label}: ${val.join(", ")}`);
      } else if (field.type === "toggle") {
        parts.push(`${field.label}: Yes`);
      } else {
        parts.push(`${field.label}: ${val}`);
      }
    });
    if (context.trim()) parts.push(`Additional context: ${context.trim()}`);
    return parts.join("\n") || topic;
  }, [visibleFields, fieldValues, context, topic]);

  const handleGenerate = async () => {
    if (!topic.trim() || !audience.trim() || !platform) {
      toast.error("Add topic, audience, and platform first.");
      return;
    }
    if (!aiModel) {
      toast.error("No AI model available. Check API Keys in Settings.");
      return;
    }

    try {
      setGenerating(true);
      setDraft("");
      setFullResponse(null);
      setSaved(false);
      const structuredContext = buildStructuredContext();
      const resp = await api.post("/generate", {
        topic,
        audience,
        platform,
        content_type: contentType,
        goal,
        research_text: structuredContext,
        cta: "NONE",
        offer_asset: "NONE",
        tone_variations: ["Direct"],
        length_constraint: "PLATFORM BEST LENGTH",
        ai_model: aiModel,
        file_ids: [],
      });

      const text = resp?.data?.draft || resp?.data?.generated_draft || resp?.data?.content || "";
      if (!text) {
        toast.error("Generation returned empty content. Try adjusting your prompt.");
        return;
      }
      setDraft(text);
      setFullResponse(resp.data);
      toast.success("Draft generated.");
    } catch (err) {
      const data = err?.response?.data;
      const message = typeof data?.detail === "string" ? data.detail : data?.error_message || "Generation failed.";
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await api.post("/history", {
        title: fullResponse?.suggested_title || `${contentType} — ${topic.substring(0, 60)}`,
        topic,
        research_material: buildStructuredContext(),
        content_type: contentType,
        platform,
        audience,
        goal,
        cta: "NONE",
        offer_asset: "NONE",
        tone_variations: ["Direct"],
        length_constraint: "PLATFORM BEST LENGTH",
        ai_model_used: aiModel,
        generated_core_angle: fullResponse?.core_angle || "",
        generated_draft: draft,
        generated_why_it_works: fullResponse?.why_it_works || "",
        generated_alternate_hooks: fullResponse?.alternate_hooks || [],
        generated_cta_variations: fullResponse?.cta_variations || [],
      });
      setSaved(true);
      toast.success("Saved to history!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(draft);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Copied to clipboard!");
  };

  // Group visible fields by section
  const groupedFields = useMemo(() => {
    const groups = [];
    let currentSection = null;
    visibleFields.forEach(field => {
      const section = field.section || "";
      if (section !== currentSection) {
        groups.push({ section, fields: [] });
        currentSection = section;
      }
      groups[groups.length - 1].fields.push(field);
    });
    return groups;
  }, [visibleFields]);

  const filledFieldCount = useMemo(() => {
    return visibleFields.filter(f => {
      const val = fieldValues[f.key];
      if (f.type === "toggle") return !!val;
      if (f.type === "chips") return Array.isArray(val) && val.length > 0;
      return val !== undefined && val !== null && val !== "";
    }).length;
  }, [visibleFields, fieldValues]);

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Header />
      <NoBusinessGuard>
        <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-6">
          <section className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-5 sm:p-6">
            <p className="text-xs uppercase tracking-wider text-[#FF4400] font-semibold">{badge}</p>
            <h1 className="text-2xl sm:text-3xl font-bold text-[var(--text-primary)] font-['Cormorant_Garamond'] mt-1">{title}</h1>
            <p className="text-sm text-[var(--text-muted)] mt-1">{description}</p>
          </section>

          <section className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-4 sm:p-6 space-y-5" data-testid="surface-generator-form">
            {/* Core fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <FieldLabel>Topic / angle</FieldLabel>
                <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="What are we creating?" className="bg-[var(--bg-base)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]" data-testid="surface-topic" />
              </div>
              <div>
                <FieldLabel>Audience</FieldLabel>
                <Input value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="Who is this for?" className="bg-[var(--bg-base)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]" data-testid="surface-audience" />
              </div>
              <div>
                <FieldLabel>{platformLabel}</FieldLabel>
                <Select value={platform} onValueChange={setPlatform}>
                  <SelectTrigger className="bg-[var(--bg-base)] border-[var(--border-strong)] text-[var(--text-primary)]" data-testid="surface-platform"><SelectValue placeholder="Select platform" /></SelectTrigger>
                  <SelectContent>
                    {platforms.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <FieldLabel>{modeLabel}</FieldLabel>
                <Select value={mode} onValueChange={setMode}>
                  <SelectTrigger className="bg-[var(--bg-base)] border-[var(--border-strong)] text-[var(--text-primary)]" data-testid="surface-mode"><SelectValue placeholder="Select mode" /></SelectTrigger>
                  <SelectContent>
                    {modes.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <FieldLabel>{objectiveLabel}</FieldLabel>
                <Select value={objective} onValueChange={setObjective}>
                  <SelectTrigger className="bg-[var(--bg-base)] border-[var(--border-strong)] text-[var(--text-primary)]" data-testid="surface-objective"><SelectValue placeholder="Select objective" /></SelectTrigger>
                  <SelectContent>
                    {objectives.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <FieldLabel>AI Model</FieldLabel>
                <Select value={aiModel} onValueChange={setAiModel}>
                  <SelectTrigger className="bg-[var(--bg-base)] border-[var(--border-strong)] text-[var(--text-primary)]" data-testid="surface-model"><SelectValue placeholder="Select model" /></SelectTrigger>
                  <SelectContent>
                    {availableModels.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Dynamic context fields */}
            {visibleFields.length > 0 && (
              <AnimatePresence mode="wait">
                <motion.div
                  key={`${platform}-${mode}`}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-4"
                >
                  {groupedFields.map(({ section, fields }, gi) => (
                    <div key={section || gi}>
                      {section && (
                        <div className="flex items-center gap-2 pt-2 pb-1">
                          <div className="h-px flex-1 bg-[var(--border)]" />
                          <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#FF4400]">{section}</span>
                          <div className="h-px flex-1 bg-[var(--border)]" />
                        </div>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {fields.map(field => (
                          <DynamicField
                            key={field.key}
                            field={field}
                            value={fieldValues[field.key]}
                            onChange={(val) => updateField(field.key, val)}
                            onToggleChip={(chip) => toggleChip(field.key, chip)}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </motion.div>
              </AnimatePresence>
            )}

            {/* Additional context */}
            <div>
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-1.5 text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors mb-2"
                data-testid="surface-advanced-toggle"
              >
                <CaretDown size={12} className={`transition-transform ${showAdvanced ? "rotate-180" : ""}`} />
                Additional context
                {filledFieldCount > 0 && (
                  <span className="px-1.5 py-0.5 bg-[#FF4400]/10 text-[#FF4400] rounded-full text-[10px] font-medium">{filledFieldCount} fields filled</span>
                )}
              </button>
              {showAdvanced && (
                <Textarea value={context} onChange={(e) => setContext(e.target.value)} className="min-h-[100px] bg-[var(--bg-base)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]" placeholder={helperText || "Add any extra context, constraints, or notes."} data-testid="surface-context" />
              )}
            </div>

            <Button onClick={handleGenerate} disabled={generating || !aiModel} className="bg-[#FF4400] hover:bg-[#FF6633] text-white gap-2" data-testid="surface-generate-btn">
              {generating ? <><SpinnerGap size={16} className="animate-spin" /> Generating...</> : <><Lightning size={16} weight="fill" /> Generate {contentType}</>}
            </Button>
          </section>

          {draft && (
            <section className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-4 sm:p-6" data-testid="surface-draft-output">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-semibold text-[var(--text-primary)]">Draft output</h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleCopy}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-[var(--border)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-colors"
                    data-testid="surface-copy-btn"
                  >
                    {copied ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
                    {copied ? "Copied" : "Copy"}
                  </button>
                  {!saved ? (
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#FF4400] hover:bg-[#FF6633] text-white transition-colors disabled:opacity-50"
                      data-testid="surface-save-btn"
                    >
                      {saving ? <SpinnerGap size={12} className="animate-spin" /> : <BookmarkSimple size={12} weight="fill" />}
                      Save to History
                    </button>
                  ) : (
                    <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-400" data-testid="surface-saved-indicator">
                      <Check size={12} weight="bold" /> Saved
                    </span>
                  )}
                </div>
              </div>
              <div className="whitespace-pre-wrap text-sm text-[var(--text-primary)] leading-relaxed bg-[var(--bg-base)] border border-[var(--border)] rounded-lg p-4 min-h-[200px]">
                {draft}
              </div>
            </section>
          )}
        </main>
      </NoBusinessGuard>
    </div>
  );
}


// ── Field label ─────────────────────────────────────────────────────────────

function FieldLabel({ children }) {
  return (
    <label className="block font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)] mb-1.5">
      {children}
    </label>
  );
}


// ── Dynamic field renderer ──────────────────────────────────────────────────

function DynamicField({ field, value, onChange, onToggleChip }) {
  const wrapperClass = field.half === false ? "md:col-span-2" : "";

  const renderInput = () => {
    switch (field.type) {
      case "text":
        return (
          <Input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder || ""}
            className="bg-[var(--bg-base)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
            data-testid={`surface-field-${field.key}`}
          />
        );

      case "textarea":
        return (
          <Textarea
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder || ""}
            rows={3}
            className="min-h-[80px] bg-[var(--bg-base)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] resize-y"
            data-testid={`surface-field-${field.key}`}
          />
        );

      case "date":
        return (
          <Input
            type="date"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            className="bg-[var(--bg-base)] border-[var(--border-strong)] text-[var(--text-primary)]"
            data-testid={`surface-field-${field.key}`}
          />
        );

      case "number":
        return (
          <Input
            type="number"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder || ""}
            min={field.min}
            max={field.max}
            className="bg-[var(--bg-base)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
            data-testid={`surface-field-${field.key}`}
          />
        );

      case "select":
        return (
          <Select value={value || ""} onValueChange={onChange}>
            <SelectTrigger className="bg-[var(--bg-base)] border-[var(--border-strong)] text-[var(--text-primary)]" data-testid={`surface-field-${field.key}`}>
              <SelectValue placeholder={field.placeholder || "Select..."} />
            </SelectTrigger>
            <SelectContent>
              {(field.options || []).map(opt => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case "toggle":
        return (
          <div className="flex items-center gap-3 h-9">
            <Switch
              checked={!!value}
              onCheckedChange={onChange}
              className="data-[state=checked]:bg-[#FF4400]"
              data-testid={`surface-field-${field.key}`}
            />
            <span className="text-sm text-[var(--text-secondary)]">{field.placeholder || "Enable"}</span>
          </div>
        );

      case "chips":
        return (
          <div className="flex flex-wrap gap-1.5" data-testid={`surface-field-${field.key}`}>
            {(field.options || []).map(chip => {
              const selected = Array.isArray(value) && value.includes(chip);
              return (
                <button
                  key={chip}
                  onClick={() => onToggleChip(chip)}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all ${
                    selected
                      ? "bg-[#FF4400]/15 border-[#FF4400]/50 text-[#FF4400]"
                      : "bg-[var(--bg-base)] border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--text-muted)]"
                  }`}
                >
                  {chip}
                </button>
              );
            })}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={wrapperClass} data-testid={`surface-fieldwrap-${field.key}`}>
      <FieldLabel>{field.label}</FieldLabel>
      {renderInput()}
      {field.hint && (
        <p className="text-[10px] text-[var(--text-muted)] mt-1">{field.hint}</p>
      )}
    </div>
  );
}
